import { PDFDocument, PDFFont, PDFPage, PDFImage, PDFForm, PDFEmbeddedPage, StandardFonts, PageSizes, rgb } from 'pdf-lib';
import { HttpClient } from '@angular/common/http';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface ImageDims {
    width: number;
    height: number;
}

export interface DomReportAssets {
    pdfDoc: PDFDocument;
    font: PDFFont;
    pages: PDFPage[];
    form: PDFForm;
    pngImage: PDFImage;
    pngDims: ImageDims;
    pngRedImage: PDFImage;
    pngRedDims: ImageDims;
    pngCheckImage: PDFImage;
    pngCheckDims: ImageDims;
    pngGraphImage: PDFImage;
    pngGraphDims: ImageDims;
    pngHoneywellImage: PDFImage;
    pngHoneywellDims: ImageDims;
    /** A snapshot of a real content page's top green report-title banner, stamped onto any page this code inserts (see drawContinuationHeader). */
    headerBanner: PDFEmbeddedPage;
}

export interface DrawNotesBlockOptions {
    pdfDoc: PDFDocument;
    pages: PDFPage[];
    pageIndex: number;
    notes: string[];
    x: number;
    startY: number;
    minY: number;
    maxWidth: number;
    font: PDFFont;
    pngHoneywellImage: PDFImage;
    pngHoneywellDims: ImageDims;
    headerBanner: PDFEmbeddedPage;
    fontSize?: number;
    lineHeight?: number;
    /** Constant count of pre-existing pages right after pageIndex that every inserted overflow page must land after, not just the first (e.g. Appliances' pre-baked appPara7-9 page). */
    reservedPagesAfterCurrent?: number;
}

export interface DrawNotesBlockResult {
    pages: PDFPage[];
    pagesAdded: number;
    lastY: number;
    /** Y position where drawing stopped on the ORIGINAL page (pageIndex), before any overflow to a continuation page - use this (not lastY) when comparing against other content on that same original page, since lastY may refer to a different physical page. */
    lastYOnStartPage: number;
}

export interface DrawNotesSectionOptions {
    pdfDoc: PDFDocument;
    pages: PDFPage[];
    /** The page the Notes bar would normally start on (wherever General Information's last field ended up). */
    page: PDFPage;
    notes: string[];
    /** Where the Notes bar would sit on `page`, computed from the last General Information field's rect. */
    barY: number;
    x: number;
    maxWidth: number;
    /** Danger-zone floor for `page` (e.g. above a static diagram, or the physical page bottom). */
    minY: number;
    font: PDFFont;
    pngHoneywellImage: PDFImage;
    pngHoneywellDims: ImageDims;
    headerBanner: PDFEmbeddedPage;
    label?: string;
    barColor?: ReturnType<typeof rgb>;
    fontSize?: number;
    lineHeight?: number;
    /** Where the bar starts on a fresh continuation page, if relocated. */
    topY?: number;
    /** Minimum lines of room required below the bar before `minY`, or the whole section relocates to a fresh page instead of stranding the bar. */
    minLinesRequired?: number;
    /** minY to use once relocated to a fresh continuation page (no danger zone there) - default 80. */
    continuationMinY?: number;
}

export interface DrawNotesSectionResult {
    pages: PDFPage[];
    pagesAdded: number;
    notesPage: PDFPage;
}

/**
 * Shared functionality for the Dominion Energy home audit PDF reports (Initial /
 * Quick-Eval and Comprehensive). Both report types fill the same style of
 * pre-designed AcroForm PDF template using pdf-lib; this base class holds the
 * common helpers, asset loading, and reusable drawing routines so each report
 * type's own class only needs to contain the page-filling logic specific to it.
 */
export abstract class DomReportBase {
    repInfo;

    constructor(protected http: HttpClient) { }

    getValue(obj, defaultValue: string = ' '): string {
        const val2Value: string = (obj === 0 || obj) ? obj : defaultValue;

        if (val2Value == null || val2Value == undefined) {
            return defaultValue;
        } else {
            return val2Value.toString();
        }
    }

    getArrayValue(obj, valname, targ, val): string {
        const val2Object = obj.find(field => field[valname] === targ);

        const val2Value: string = val2Object ? val2Object[val] : ' ';

        if (val2Value == null || val2Value == undefined) {
            return ' ';
        } else {
            return val2Value.toString();
        }
    }

    formatPhone(phoneValue: string): string {
        if (phoneValue === undefined) {
            return ' ';
        }

        try {
            let phoneNumber = parsePhoneNumberFromString(phoneValue, 'US');
            if (phoneNumber === undefined) {
                return phoneValue;
            } else {
                return phoneNumber.formatNational();
            }
        } catch (error) {
            return phoneValue;
        }
    }

    /**
     * Normalizes a section's Notes field into a clean string[] for rendering.
     * Tolerates a missing section/key, null, a single string value, or an
     * array containing empty/null entries - all cases collapse to a safe
     * array of non-empty, trimmed strings.
     */
    getNotesArray(section: any, key: string = 'Notes'): string[] {
        if (!section) {
            return [];
        }

        const raw = section[key];
        if (!raw) {
            return [];
        }

        const values = Array.isArray(raw) ? raw : [raw];

        return values
            .filter(value => value !== null && value !== undefined)
            .map(value => value.toString().trim())
            .filter(value => value.length > 0);
    }

    getUsageBarMetrics(currentUsage: number | string, estimatedSavings: number | string): {
        currentUsage: number;
        estimatedSavings: number;
        proposedUsage: number;
        currentUsageWidth: number;
        proposedUsageWidth: number;
    } {
        const current = Number(currentUsage) || 0;
        const savings = Number(estimatedSavings) || 0;

        let currentUsageWidth = (current / 40000) * 4 * 121.25;
        if (current > 0 && currentUsageWidth < 1) {
            currentUsageWidth = 1;
        }

        if (savings <= 0) {
            return {
                currentUsage: current,
                estimatedSavings: savings,
                proposedUsage: 0,
                currentUsageWidth,
                proposedUsageWidth: 0
            };
        }

        let proposedUsage = Math.max(current - savings, 0);
        let proposedUsageWidth = (proposedUsage / 40000) * 4 * 121.25;

        if (proposedUsage > 0 && proposedUsageWidth < 1) {
            proposedUsageWidth = 1;
        }

        return {
            currentUsage: current,
            estimatedSavings: savings,
            proposedUsage,
            currentUsageWidth,
            proposedUsageWidth
        };
    }

    /**
     * Loads the given AcroForm PDF template plus the shared images used across
     * both report types, and returns everything needed to fill it in.
     */
    protected async loadReportAssets(pdfPath: string): Promise<DomReportAssets> {
        const existingPdfBytes = await this.http.get(pdfPath, { responseType: 'arraybuffer' }).toPromise();
        if (!existingPdfBytes) {
            throw new Error('Failed to load PDF');
        }
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const pages = pdfDoc.getPages();
        const form = pdfDoc.getForm();

        // load images
        const thumbImagePath = "assets/DOM1/thumbsupGreen.png";
        const existingImageBytes = await this.http.get(thumbImagePath, { responseType: 'arraybuffer' }).toPromise();
        if (!existingImageBytes) {
            throw new Error('Failed to load Image');
        }

        const pngImage = await pdfDoc.embedPng(existingImageBytes);
        const pngDims = pngImage.scale(1.0);

        const redImagePath = "assets/DOM1/exclamationRed.png";
        const existingRedImageBytes = await this.http.get(redImagePath, { responseType: 'arraybuffer' }).toPromise();
        if (!existingRedImageBytes) {
            throw new Error('Failed to load red Image');
        }

        const pngRedImage = await pdfDoc.embedPng(existingRedImageBytes);
        const pngRedDims = pngImage.scale(0.8);

        const checkImagePath = "assets/DOM1/GreenCheckMark.png";
        const existingCheckImageBytes = await this.http.get(checkImagePath, { responseType: 'arraybuffer' }).toPromise();
        if (!existingCheckImageBytes) {
            throw new Error('Failed to load check mark Image');
        }

        const pngCheckImage = await pdfDoc.embedPng(existingCheckImageBytes);
        const pngCheckDims = pngImage.scale(0.38);

        const graphImagePath = "assets/DOM1/usagegraph.png";
        const existingGraphImageBytes = await this.http.get(graphImagePath, { responseType: 'arraybuffer' }).toPromise();
        if (!existingGraphImageBytes) {
            throw new Error('Failed to load Graph Image');
        }

        const pngGraphImage = await pdfDoc.embedPng(existingGraphImageBytes);
        const pngGraphDims = pngGraphImage.scale(0.53);

        const honeywellImagePath = "assets/honeywell_logo.png";
        const existingHoneywellImageBytes = await this.http.get(honeywellImagePath, { responseType: 'arraybuffer' }).toPromise();
        if (!existingHoneywellImageBytes) {
            throw new Error('Failed to load logo Image');
        }

        const pngHoneywellImage = await pdfDoc.embedPng(existingHoneywellImageBytes);
        const pngHoneywellDims = pngHoneywellImage.scale(0.53);

        // Snapshot the top banner strip of an existing content page (static
        // template artwork - title text, house icon, green background, plus
        // the report's own filled-in service date) so it can be stamped onto
        // any blank page this code inserts. pdf-lib's embedPage only reads
        // the source page's content lazily at pdfDoc.save() time (not when
        // this is called) - by then form.flatten() has already baked the
        // DateOfService widget's text into this page, so it's captured too.
        const bannerSourcePage = pages[2];
        const { width: bannerPageWidth, height: bannerPageHeight } = bannerSourcePage.getSize();
        // Stop above page 2's own "Estimated Energy Savings"/"Est. Total Rebate
        // Incentive" boxes (their fields start around y=650-662) so only the
        // green title bar + date line get captured.
        const headerBannerBottom = 695;
        const headerBanner = await pdfDoc.embedPage(bannerSourcePage, {
            left: 0,
            right: bannerPageWidth,
            bottom: headerBannerBottom,
            top: bannerPageHeight
        });

        return {
            pdfDoc, font, pages, form,
            pngImage, pngDims,
            pngRedImage, pngRedDims,
            pngCheckImage, pngCheckDims,
            pngGraphImage, pngGraphDims,
            pngHoneywellImage, pngHoneywellDims,
            headerBanner
        };
    }

    /**
     * Greedy word-wrap of `text` into lines that each fit within `maxWidth` at
     * `fontSize`, using the given embedded font's metrics. Handles embedded
     * newlines (\n, \r\n, \\n) by splitting into separate lines/paragraphs,
     * and tab characters (\t, \\t) by expanding each to 5 spaces. A single
     * word wider than `maxWidth` on its own (e.g. a long URL) is force-broken
     * at the character level so it still wraps instead of running off the page.
     * Returns an empty array for null/empty/whitespace-only text.
     */
    wrapTextLines(font: PDFFont, text: string, maxWidth: number, fontSize: number): string[] {
        if (!text) {
            return [];
        }

        const normalized = text
            .replace(/\\r\\n|\\n|\r\n/g, '\n')
            .replace(/\\t|\t/g, '     ');

        if (normalized.trim().length === 0) {
            return [];
        }

        const paragraphs = normalized.split('\n');
        const lines: string[] = [];

        paragraphs.forEach(paragraph => {
            const words = paragraph.split(/\s+/).filter(word => word.length > 0);
            if (words.length === 0) {
                lines.push('');
                return;
            }

            const fits = (candidate: string) => font.widthOfTextAtSize(candidate, fontSize) <= maxWidth;
            let currentLine = '';

            words.forEach(word => {
                if (fits(word)) {
                    const candidateLine = currentLine ? currentLine + ' ' + word : word;
                    if (fits(candidateLine)) {
                        currentLine = candidateLine;
                    } else {
                        lines.push(currentLine);
                        currentLine = word;
                    }
                    return;
                }

                // Word alone is wider than maxWidth - break it at the character
                // level instead of letting it run off the page.
                if (currentLine) {
                    lines.push(currentLine);
                    currentLine = '';
                }

                let remainder = word;
                while (remainder.length > 0) {
                    let splitAt = remainder.length;
                    while (splitAt > 1 && !fits(remainder.slice(0, splitAt))) {
                        splitAt--;
                    }
                    lines.push(remainder.slice(0, splitAt));
                    remainder = remainder.slice(splitAt);
                }
            });

            if (currentLine) {
                lines.push(currentLine);
            }
        });

        while (lines.length > 0 && lines[lines.length - 1] === '') {
            lines.pop();
        }

        return lines;
    }

    /**
     * Draws `items` starting at (`x`, `startY`), one below the other. Unlike
     * a plain forEach + fixed decrement, this splits each item on embedded
     * `\n` characters and decrements y per RENDERED line (not per array
     * entry), so a multi-line entry doesn't get overlapped by whatever's
     * drawn right after it. Returns the y position after the last line.
     */
    drawMultilineList(page: PDFPage, x: number, startY: number, items: string[], fontSize: number = 10, lineHeight: number = 12): number {
        let y = startY;
        (items ?? []).forEach(item => {
            (item ?? '')
                .replace(/\\r\\n|\\n|\r\n/g, '\n')
                .replace(/\\t|\t/g, '     ')
                .split('\n')
                .forEach(line => {
                    page.moveTo(x, y);
                    page.drawText(line, { size: fontSize });
                    y -= lineHeight;
                });
        });
        return y;
    }

    /**
     * Draws the top green report-title banner (stamped from a real content
     * page - including the report's own service date, see loadReportAssets)
     * plus the "Powered By" + Honeywell logo footer used on any blank
     * continuation page inserted when content overflows a page.
     */
    drawContinuationHeader(page: PDFPage, pngHoneywellImage: PDFImage, pngHoneywellDims: ImageDims, headerBanner: PDFEmbeddedPage): void {
        const { height: pageHeight } = page.getSize();
        page.drawPage(headerBanner, {
            x: 0,
            y: pageHeight - headerBanner.height,
            width: headerBanner.width,
            height: headerBanner.height
        });

        page.moveTo(35, 25);
        page.drawText("Powered By", { size: 9 });

        page.drawImage(pngHoneywellImage, {
            x: 90,
            y: 20,
            width: pngHoneywellDims.width,
            height: pngHoneywellDims.height,
        });
    }

    /**
     * Draws a list of Notes strings (each word-wrapped to fit `maxWidth`)
     * starting at (`x`, `startY`) on the page at `pageIndex`. When a line would
     * fall below `minY`, a blank continuation page is inserted immediately
     * after the current page and drawing continues from the top of it.
     * Returns the refreshed `pages` array and how many pages were inserted so
     * the caller can update its own page-offset bookkeeping.
     */
    drawNotesBlock(opts: DrawNotesBlockOptions): DrawNotesBlockResult {
        const { pdfDoc, notes, x, startY, minY, maxWidth, font, pngHoneywellImage, pngHoneywellDims, headerBanner } = opts;
        const fontSize = opts.fontSize ?? 10;
        const lineHeight = opts.lineHeight ?? 12;

        let pages = opts.pages;
        let page = pages[opts.pageIndex];
        let y = startY;
        let pagesAdded = 0;
        let lastYOnStartPage = startY;

        const notesList = notes ?? [];
        const lines: string[] = [];
        notesList.forEach((note, idx) => {
            lines.push(...this.wrapTextLines(font, note, maxWidth, fontSize));
            // blank spacer line between separate Notes entries so multiple
            // values (e.g. repeated per-area notes) don't run together
            if (idx < notesList.length - 1) {
                lines.push('');
            }
        });

        lines.forEach(line => {
            if (y < minY) {
                // Insert the blank continuation page immediately AFTER the
                // current page (pageIndex + pagesAdded), not at its index -
                // inserting at the current index would push the page we've
                // already drawn on backward, reversing the note order.
                const insertAt = opts.pageIndex + (opts.reservedPagesAfterCurrent ?? 0) + pagesAdded + 1;
                pdfDoc.insertPage(insertAt, PageSizes.Letter);
                pages = pdfDoc.getPages();
                page = pages[insertAt];
                // stay below the stamped green banner (bottom edge y=695)
                y = 670;
                pagesAdded++;
                this.drawContinuationHeader(page, pngHoneywellImage, pngHoneywellDims, headerBanner);
            }

            if (line.length > 0) {
                page.moveTo(x, y);
                page.drawText(line, { size: fontSize });
            }

            if (pagesAdded === 0) {
                lastYOnStartPage = y - lineHeight;
            }

            y -= lineHeight;
        });

        return { pages, pagesAdded, lastY: y, lastYOnStartPage };
    }

    /**
     * How much `fieldNames`' `referenceFieldName` member on `page` would
     * need to shift down to clear `contentBottomY` (0 if already clear).
     */
    getRequiredShift(form: PDFForm, referenceFieldName: string, contentBottomY: number, page?: PDFPage, margin: number = 10): number {
        const pageRef = page?.ref?.toString();
        const widget = form.getTextField(referenceFieldName).acroField.getWidgets().find(w => !pageRef || w.P()?.toString() === pageRef);
        if (!widget) {
            return 0;
        }
        const rect = widget.getRectangle();
        return Math.max((rect.y + rect.height + margin) - contentBottomY, 0);
    }

    /**
     * If notes/measures content on this page ended below where
     * `referenceFieldName` currently starts, shifts every field in
     * `fieldNames` down by the overflow amount so the rest of the page's
     * static content moves clear of the overflow instead of being overlapped.
     * Returns the shift actually applied (0 if there was no overflow).
     * Capped at `maxShift` only - notes must never be truncated, so the
     * shifted fields (including the last paragraph/bullet on the page) take
     * whatever room is needed. When `page` is given, only widgets rendered
     * on that page are considered/moved - required for fields whose
     * acroField is reused (shares a widget) across more than one page in
     * this template.
     */
    shiftFieldsBelowOverflow(form: PDFForm, referenceFieldName: string, fieldNames: string[], contentBottomY: number, page?: PDFPage, margin: number = 10, maxShift: number = 2000): number {
        const overflow = Math.min(this.getRequiredShift(form, referenceFieldName, contentBottomY, page, margin), maxShift);
        if (overflow <= 0) {
            return 0;
        }

        const pageRef = page?.ref?.toString();
        fieldNames.forEach(name => {
            form.getTextField(name).acroField.getWidgets()
                .filter(widget => !pageRef || widget.P()?.toString() === pageRef)
                .forEach(widget => {
                    const rect = widget.getRectangle();
                    widget.setRectangle({ x: rect.x, y: rect.y - overflow, width: rect.width, height: rect.height });
                });
        });

        return overflow;
    }

    /**
     * Relocates every field in `fieldNames` from `fromPage` onto a newly
     * inserted blank continuation page (same "Powered By Honeywell" banner
     * style as drawNotesBlock's own overflow pages), preserving their
     * relative vertical spacing but anchored near the top of the new page.
     * Returns the new page, or undefined if none of `fieldNames` were
     * actually found on `fromPage` (in which case no page is inserted).
     */
    moveFieldsToContinuationPage(pdfDoc: PDFDocument, form: PDFForm, fieldNames: string[], fromPage: PDFPage, insertAt: number, pngHoneywellImage: PDFImage, pngHoneywellDims: ImageDims, headerBanner: PDFEmbeddedPage, topY: number = 670): PDFPage | undefined {
        const fromPageRef = fromPage.ref.toString();
        const tops: number[] = [];
        fieldNames.forEach(name => {
            const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === fromPageRef);
            if (widget) {
                const rect = widget.getRectangle();
                tops.push(rect.y + rect.height);
            }
        });
        if (tops.length === 0) {
            return undefined;
        }

        pdfDoc.insertPage(insertAt, PageSizes.Letter);
        const newPage = pdfDoc.getPages()[insertAt];
        this.drawContinuationHeader(newPage, pngHoneywellImage, pngHoneywellDims, headerBanner);

        const delta = topY - Math.max(...tops);

        fieldNames.forEach(name => {
            const field = form.getTextField(name);
            const widget = field.acroField.getWidgets().find(w => w.P()?.toString() === fromPageRef);
            if (!widget) {
                return;
            }
            const rect = widget.getRectangle();
            fromPage.node.removeAnnot(field.ref);
            newPage.node.addAnnot(field.ref);
            widget.setP(newPage.ref);
            widget.setRectangle({ x: rect.x, y: rect.y + delta, width: rect.width, height: rect.height });
        });

        return newPage;
    }

    /**
     * Relocates `fieldNames` from `fromPage` onto an EXISTING `targetPage`
     * (unlike `moveFieldsToContinuationPage`, no new page is inserted) -
     * for templates where a section's fields are pre-split across more
     * than one physical page (e.g. Appliances' appPara7-9 living on their
     * own pre-baked page right after the main one), so an overflowing tail
     * can merge onto that already-existing next page instead of stranding
     * it alone on a brand new blank one. `downstreamFieldNames` (whatever
     * already lives on `targetPage`) are pushed down by however much
     * vertical room the incoming fields need, then the incoming fields are
     * stacked from `topY` downward, in the order given, onto `targetPage`.
     */
    mergeFieldsOntoPage(form: PDFForm, fieldNames: string[], fromPage: PDFPage, targetPage: PDFPage, downstreamFieldNames: string[], gap: number = 10, topY: number = 670): void {
        const fromPageRef = fromPage.ref.toString();
        const targetPageRef = targetPage.ref.toString();

        const incoming: { name: string; rect: { x: number; y: number; width: number; height: number } }[] = [];
        fieldNames.forEach(name => {
            const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === fromPageRef);
            if (widget) {
                incoming.push({ name, rect: widget.getRectangle() });
            }
        });
        if (incoming.length === 0) {
            return;
        }

        // Y where the incoming block ends once stacked from topY downward
        let incomingBottom = topY;
        incoming.forEach(({ rect }) => {
            incomingBottom -= rect.height + gap;
        });

        // Shift downstream content down by exactly enough to clear the
        // incoming block - based on its CURRENT top, not an assumed
        // starting point, since it may already start below topY.
        let downstreamTop: number | undefined;
        downstreamFieldNames.forEach(name => {
            const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === targetPageRef);
            if (widget) {
                const rect = widget.getRectangle();
                const top = rect.y + rect.height;
                downstreamTop = downstreamTop === undefined ? top : Math.max(downstreamTop, top);
            }
        });

        const shiftAmount = downstreamTop === undefined ? 0 : downstreamTop - incomingBottom;
        if (shiftAmount > 0) {
            downstreamFieldNames.forEach(name => {
                const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === targetPageRef);
                if (!widget) {
                    return;
                }
                const rect = widget.getRectangle();
                widget.setRectangle({ x: rect.x, y: rect.y - shiftAmount, width: rect.width, height: rect.height });
            });
        }

        let y = topY;
        incoming.forEach(({ name, rect }) => {
            y -= rect.height;
            const field = form.getTextField(name);
            const widget = field.acroField.getWidgets().find(w => w.P()?.toString() === fromPageRef);
            if (!widget) {
                return;
            }
            fromPage.node.removeAnnot(field.ref);
            targetPage.node.addAnnot(field.ref);
            widget.setP(targetPage.ref);
            widget.setRectangle({ x: rect.x, y, width: rect.width, height: rect.height });
            y -= gap;
        });
    }

    /**
     * "Slides" a group of fields down by the full amount needed to clear
     * `contentBottomY`, then relocates onto a newly-inserted continuation
     * page (right after `page`) only whichever specific fields' new
     * position would fall below `bottomMargin` - preserving relative order
     * among themselves. Fields near the top of the group (e.g. the section
     * header) that still fit stay in place, so this reads as a true slide
     * with the overflowing tail continuing on the next page, rather than
     * relocating the whole group as an all-or-nothing block. However, if
     * the reference field (assumed to be `fieldNames[0]`, e.g. the section
     * header) OR the field immediately following it (`fieldNames[1]`, the
     * first real content a reader would see right under the header) would
     * itself end up in the overflowing set, a partial slide would strand
     * the header alone with nothing meaningful under it (or an empty gap
     * before whatever survives) - so in that case the ENTIRE group
     * relocates together instead. Returns the shift applied and, if
     * anything overflowed, the page it landed on.
     */
    slideFieldsDown(pdfDoc: PDFDocument, form: PDFForm, referenceFieldName: string, fieldNames: string[], contentBottomY: number, page: PDFPage, insertAt: number, pngHoneywellImage: PDFImage, pngHoneywellDims: ImageDims, headerBanner: PDFEmbeddedPage, margin: number = 10, bottomMargin: number = 40, topY: number = 670): { shift: number; overflowPage?: PDFPage } {
        const shift = this.shiftFieldsBelowOverflow(form, referenceFieldName, fieldNames, contentBottomY, page, margin);
        if (shift <= 0) {
            return { shift: 0 };
        }

        const pageRef = page.ref.toString();

        const overflowNames: string[] = [];
        fieldNames.forEach(name => {
            const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === pageRef);
            if (widget && widget.getRectangle().y < bottomMargin) {
                overflowNames.push(name);
            }
        });

        if (overflowNames.length === 0) {
            return { shift };
        }

        const firstContentFieldName = fieldNames.find(name => name !== referenceFieldName);
        const strandsHeader = overflowNames.includes(referenceFieldName)
            || (firstContentFieldName !== undefined && overflowNames.includes(firstContentFieldName));

        const namesToRelocate = strandsHeader ? fieldNames : overflowNames;
        const overflowPage = this.moveFieldsToContinuationPage(pdfDoc, form, namesToRelocate, page, insertAt, pngHoneywellImage, pngHoneywellDims, headerBanner, topY);
        return { shift, overflowPage };
    }

    /**
     * Draws a bullet glyph immediately to the left of each field in
     * `fieldNames`'s CURRENT position on `page`. The template's own bullet
     * glyphs are baked into its static background at fixed positions and
     * don't move with a field, so call this after any shift/relocation
     * (with the template's static glyphs removed) to keep bullets attached
     * to their text.
     */
    drawBulletsForFields(form: PDFForm, page: PDFPage, fieldNames: string[], bulletOffsetX: number = -15, fontSize: number = 10): void {
        const pageRef = page.ref.toString();
        fieldNames.forEach(name => {
            const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === pageRef);
            if (!widget) {
                return;
            }
            const rect = widget.getRectangle();
            page.moveTo(rect.x + bulletOffsetX, rect.y + rect.height - fontSize);
            page.drawText('\u2022', { size: fontSize });
        });
    }

    /**
     * Covers each field's ORIGINAL (un-shifted) static bullet glyph with
     * white, for templates that still have bullets baked into the page's
     * static background (sections never previously touched by this
     * feature) - so `drawBulletsForFields` can redraw a fresh one later
     * wherever the field ends up after a shift, without a stale glyph left
     * behind at the old spot. Must run BEFORE any shift is applied.
     */
    coverStaticBullets(form: PDFForm, fieldNames: string[], page: PDFPage, bulletOffsetX: number = -15, fontSize: number = 10): void {
        const pageRef = page.ref.toString();
        fieldNames.forEach(name => {
            const widget = form.getTextField(name).acroField.getWidgets().find(w => w.P()?.toString() === pageRef);
            if (!widget) {
                return;
            }
            const rect = widget.getRectangle();
            page.drawRectangle({ x: rect.x + bulletOffsetX - 2, y: rect.y + rect.height - fontSize - 2, width: fontSize + 4, height: fontSize + 4, color: rgb(1, 1, 1) });
        });
    }

    /**
     * Returns the rectangle of `fieldName`'s widget on `page`, or undefined
     * if that field has no widget there.
     */
    getFieldRectOnPage(form: PDFForm, fieldName: string, page: PDFPage): { x: number; y: number; width: number; height: number } | undefined {
        const pageRef = page.ref.toString();
        const widget = form.getTextField(fieldName).acroField.getWidgets().find(w => w.P()?.toString() === pageRef);
        return widget?.getRectangle();
    }

    /**
     * Draws the section title bar (e.g. "General Information", baked into
     * the page's static background originally and since removed from the
     * template directly) at `fieldName`'s CURRENT position, on whichever of
     * `candidatePages` it's actually on. Called unconditionally (not just
     * after a shift) since the static bar no longer exists at all.
     */
    drawHeaderBarWhereverFieldIs(form: PDFForm, fieldName: string, candidatePages: (PDFPage | undefined)[], barX: number = 123.5, barWidth: number = 450, barHeight: number = 25): void {
        for (const candidate of candidatePages) {
            if (!candidate) {
                continue;
            }
            const rect = this.getFieldRectOnPage(form, fieldName, candidate);
            if (rect) {
                const barY = rect.y - (barHeight - rect.height) / 2;
                candidate.drawRectangle({ x: barX, y: barY, width: barWidth, height: barHeight, color: rgb(0.44, 0.50, 0.53) });
                return;
            }
        }
    }

    /**
     * Covers `fieldName`'s ORIGINAL (un-shifted) "General Information" bar
     * position with white, for templates that still have that bar baked
     * into the page's static background (sections never previously
     * touched by the Notes feature) - so a fresh bar can be redrawn later,
     * via `drawHeaderBarWhereverFieldIs`, wherever the field ends up after
     * any Potential-Measures-driven shift, without a stale copy left
     * behind at the old spot. Safe to call even when no static bar is
     * actually there (draws over blank page background) or when the field
     * never shifts (the fresh bar redraw simply lands on top, unchanged).
     * Must run BEFORE any shift is applied to `fieldName`.
     */
    coverStaticHeaderBar(form: PDFForm, fieldName: string, page: PDFPage, barX: number = 123.5, barWidth: number = 450, barHeight: number = 25): void {
        const rect = this.getFieldRectOnPage(form, fieldName, page);
        if (!rect) {
            return;
        }
        const barY = rect.y - (barHeight - rect.height) / 2;
        page.drawRectangle({ x: barX, y: barY, width: barWidth, height: barHeight, color: rgb(1, 1, 1) });
    }

    /**
     * Draws a colored title bar with white label text - shared by
     * "Potential Measures" (rust/orange, the default) and "Notes" (grey,
     * matching "General Information"'s bar) section headers.
     */
    drawSectionBar(page: PDFPage, label: string, y: number, barX: number = 123.5, barWidth: number = 450, barHeight: number = 25, color = rgb(0.76, 0.28, 0.07)): void {
        page.drawRectangle({ x: barX, y, width: barWidth, height: barHeight, color });
        page.moveTo(barX + 10, y + 7);
        page.drawText(label, { color: rgb(1, 1, 1), size: 14 });
    }

    /**
     * Draws a "Notes" (or similar) bar + wrapped text block, but first
     * checks whether at least `minLinesRequired` lines of text would fit
     * below the bar before `minY` - if not, the bar would just sit there
     * with nothing meaningful under it (the actual text jumping straight
     * to a continuation page via drawNotesBlock's own pagination), so the
     * ENTIRE section (bar + text) relocates to a fresh continuation page
     * together instead.
     */
    drawNotesSection(opts: DrawNotesSectionOptions): DrawNotesSectionResult {
        const label = opts.label ?? 'Notes';
        const barHeight = 25;
        const lineHeight = opts.lineHeight ?? 12;
        const fontSize = opts.fontSize ?? 10;
        const topY = opts.topY ?? 670;
        const minLinesRequired = opts.minLinesRequired ?? 2;
        const barColor = opts.barColor ?? rgb(0.44, 0.50, 0.53);
        const continuationMinY = opts.continuationMinY ?? 80;

        let pages = opts.pages;
        let notesPage = opts.page;
        let barY = opts.barY;
        let minY = opts.minY;
        let pagesAdded = 0;

        const roomBelowBar = (barY - 10) - opts.minY;
        if (roomBelowBar < minLinesRequired * lineHeight) {
            const insertAt = this.getPageIndex(opts.pdfDoc, opts.page) + 1;
            opts.pdfDoc.insertPage(insertAt, PageSizes.Letter);
            pages = opts.pdfDoc.getPages();
            notesPage = pages[insertAt];
            this.drawContinuationHeader(notesPage, opts.pngHoneywellImage, opts.pngHoneywellDims, opts.headerBanner);
            barY = topY - barHeight;
            minY = continuationMinY;
            pagesAdded++;
        }

        this.drawSectionBar(notesPage, label, barY, 123.5, 450, barHeight, barColor);

        const result = this.drawNotesBlock({
            pdfDoc: opts.pdfDoc,
            pages,
            pageIndex: this.getPageIndex(opts.pdfDoc, notesPage),
            notes: opts.notes,
            x: opts.x,
            startY: barY - 10,
            minY,
            maxWidth: opts.maxWidth,
            font: opts.font,
            pngHoneywellImage: opts.pngHoneywellImage,
            pngHoneywellDims: opts.pngHoneywellDims,
            headerBanner: opts.headerBanner,
            fontSize,
            lineHeight
        });

        return { pages: result.pages, pagesAdded: pagesAdded + result.pagesAdded, notesPage };
    }

    /**
     * Finds `page`'s current index in `pdfDoc`'s page list - needed after
     * a field/page relocation to know which `pageIndex` to hand to
     * `drawNotesBlock`, since a page's array position can shift as other
     * pages get inserted elsewhere in the document.
     */
    getPageIndex(pdfDoc: PDFDocument, page: PDFPage): number {
        return pdfDoc.getPages().findIndex(p => p.ref.toString() === page.ref.toString());
    }
}
